import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
    User, Settings, Moon, Sun, Info, Users, ChevronRight, LayoutDashboard, FileText, Contact, LogOut,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/auth-store';
import { useAppStore } from '../../stores/app-store';
import { supabase } from '../../lib/supabase/client';

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, user, signOut: storeSignOut } = useAuthStore();
  const { mode, setMode, reset: resetAppStore } = useAppStore();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close menu
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const isPartner = (profile as any)?.user_type === 'partner';
  const isDark = mode === 'dark';

  // Fetch partner profile if user is a partner
  const { data: partnerProfile } = useQuery({
    queryKey: ['partner-profile-header', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('partners')
        .select('organization_name, logo_url')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        return null;
      }

      return data as { organization_name: string; logo_url: string | null } | null;
    },
    enabled: !!user?.id && isPartner,
  });

  if (!profile && user) {
    return (
      <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
    );
  }

  if (!user) return null;

  const displayName = isPartner && partnerProfile
    ? partnerProfile.organization_name
    : profile?.fullname || user?.email?.split('@')[0] || 'User';
  const displayImage = isPartner && partnerProfile
    ? partnerProfile.logo_url
    : profile?.image_url || null;
  const regularMenuItems = [
    { to: '/account', icon: User, label: t('userMenu.myAccount', 'My Account') },
    { to: '/contacts', icon: Users, label: t('nav.contacts', 'My Contacts') },
    { to: '/settings', icon: Settings, label: t('userMenu.settings', 'Settings') },
  ];

  const partnerMenuItems = [
    { to: '/partner/dashboard?view=overview', icon: LayoutDashboard, label: t('partner.dashboard.title', 'Partner Dashboard') },
    { to: '/partner/dashboard?view=pages', icon: FileText, label: t('partner.dashboard.nav.pages', 'Memorial Pages') },
    { to: '/partner/dashboard?view=contacts', icon: Contact, label: t('partner.dashboard.nav.contacts', 'Contacts') },
    { to: '/partner/profile', icon: User, label: t('partner.dashboard.nav.profile', 'Profile') },
    { to: '/partner/dashboard?view=settings', icon: Settings, label: t('partner.dashboard.nav.settings', 'Settings') },
  ];

  const menuItems = isPartner ? partnerMenuItems : regularMenuItems;

  const handleSignOut = async () => {
    const confirmed = window.confirm(
      t('settings.signOutMessage', 'Are you sure you want to sign out?')
    );
    if (!confirmed) return;

    setIsOpen(false);
    try {
      storeSignOut();
      resetAppStore();
      await supabase.auth.signOut();
      navigate('/', { replace: true });
      window.location.reload();
    } catch {
      toast.error(t('common.error', 'Something went wrong'));
    }
  };

  // ── Menu item helper ───────────────────────────────────────────────────────
  const MenuItem = ({
    to, icon: Icon, label,
  }: {
    to: string;
    icon: React.ElementType;
    label: string;
  }) => (
    <Link
      to={to}
      onClick={() => setIsOpen(false)}
      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent group"
    >
      <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
      <span className="flex-1">{label}</span>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" />
    </Link>
  );

  const Divider = () => <div className="my-1 h-px bg-border/60 mx-2" />;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-accent"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="h-10 w-10 overflow-hidden rounded-full bg-primary/10 ring-2 ring-primary/20">
          {displayImage ? (
            <img src={displayImage} alt={displayName} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-lg font-bold text-primary">
              {displayName?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
        </div>
        <span className="hidden text-sm font-medium text-foreground md:inline">
          {displayName}
        </span>
        <svg
          className={`hidden h-4 w-4 text-muted-foreground transition-transform md:inline ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute end-0 z-50 mt-2 w-72 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 rounded-xl border border-border bg-card shadow-xl duration-200">

          {/* ── User Info Header ── */}
          <div className="border-b border-border bg-muted/30 p-4 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 overflow-hidden rounded-full bg-primary/10 ring-2 ring-primary/20 flex-shrink-0">
                {displayImage ? (
                  <img src={displayImage} alt={displayName} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xl font-bold text-primary">
                    {displayName?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate font-semibold text-foreground">{displayName}</p>
                {isPartner ? (
                  profile?.fullname && profile.fullname !== displayName && (
                    <p className="truncate text-xs text-muted-foreground">{profile.fullname}</p>
                  )
                ) : (
                  profile?.user_name && (
                    <p className="truncate text-xs text-muted-foreground">@{profile.user_name}</p>
                  )
                )}
              </div>
            </div>
          </div>

          {/* ── Menu Body ── */}
          <div className="py-1.5">
            {menuItems.map((item) => (
              <MenuItem key={item.to} to={item.to} icon={item.icon} label={item.label} />
            ))}
            <button
              onClick={() => setMode(isDark ? 'light' : 'dark')}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent group"
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              )}
              <span className="flex-1 text-left">
                {isDark ? t('sidebar.lightMode', 'Light Mode') : t('sidebar.darkMode', 'Dark Mode')}
              </span>
              <span className={`flex h-5 w-9 items-center rounded-full px-0.5 transition-colors ${isDark ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                <span className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${isDark ? 'translate-x-4' : 'translate-x-0'}`} />
              </span>
            </button>
            <Divider />
            <MenuItem to="/about" icon={Info} label={t('sidebar.about', 'About Candle Legacy')} />
            <Divider />
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10 group"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">{t('settings.signOut', 'Sign Out')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
